import { useFormikContext } from 'formik';
import { clsx } from 'clsx';
import {
  CARGO_VESSEL_TYPES,
  CARGO_VESSEL_TYPE_DEFAULTS,
  type CargoVesselType,
} from '@/constants/cargoList.constants';
import type { CreateCargoFormValues } from '@/types/cargoList.types';

interface VesselTypeQuickSelectProps {
  /** `null` until the user picks one - the form then behaves exactly as before. */
  value: CargoVesselType | null;
  onSelect: (vesselType: CargoVesselType) => void;
}

/**
 * VLCC | Suezmax quick-select that prefills the four fields whose values are
 * fixed per vessel class. Everything it writes stays editable, and the fields it
 * does not name (charterer, trade, laycan, broker, rate assessor) are untouched.
 *
 * The selected type is not a form value - it never reaches the API - so it lives
 * in the modal's own state and is passed in.
 */
export function VesselTypeQuickSelect({ value, onSelect }: VesselTypeQuickSelectProps) {
  const { setValues } = useFormikContext<CreateCargoFormValues>();

  const applyDefaults = (vesselType: CargoVesselType) => {
    // One setValues rather than four setFieldValue calls: setFieldValue validates
    // against the last committed render's values, and no re-render happens
    // between calls in a single handler. Four of them would each validate a stale
    // base, leaving e.g. "Cargo size is required" under a field now showing
    // 280000. setValues validates the resolved object once.
    setValues((previous) => ({ ...previous, ...CARGO_VESSEL_TYPE_DEFAULTS[vesselType] }));
    onSelect(vesselType);
  };

  return (
    <div className="space-y-1.5">
      <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Vessel Type
      </span>

      <div
        role="group"
        aria-label="Vessel Type"
        className="inline-flex rounded-lg border border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-800"
      >
        {CARGO_VESSEL_TYPES.map((vesselType, index) => (
          <button
            // Without an explicit type this submits the surrounding <Form>.
            type="button"
            key={vesselType}
            aria-pressed={value === vesselType}
            onClick={() => applyDefaults(vesselType)}
            className={clsx(
              'px-8 py-2 text-sm font-medium transition-colors',
              index === 0 ? 'rounded-l-lg' : 'rounded-r-lg',
              value === vesselType
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            )}
          >
            {vesselType}
          </button>
        ))}
      </div>

      {value && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Defaults applied — edit any field below if needed
        </p>
      )}
    </div>
  );
}
